import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Contrast, Monitor, Moon, Sun } from 'lucide-react'

import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type { MarketingThemePreference } from '@/hooks/use-marketing-theme'
import { cn } from '@/lib/utils'

type Appearance = 'light' | 'dark' | 'system'
type ResolvedAppearance = 'light' | 'dark'

const toggleItemClassName =
  'h-6 w-6 min-w-6 !rounded-full px-0 text-muted-foreground hover:bg-transparent hover:text-foreground data-[state=on]:bg-foreground data-[state=on]:text-background'

const guestTrackFilledClassName =
  'border-[var(--marketing-border,var(--border))] bg-[var(--marketing-bg,var(--background))]'

const MARKETING_CYCLE: MarketingThemePreference[] = ['light', 'dark', 'system']

function nextMarketingPreference(current: MarketingThemePreference): MarketingThemePreference {
  const index = MARKETING_CYCLE.indexOf(current)
  return MARKETING_CYCLE[(index + 1) % MARKETING_CYCLE.length] ?? 'light'
}

function marketingPreferenceLabel(preference: MarketingThemePreference): string {
  if (preference === 'light') return 'Light'
  if (preference === 'dark') return 'Dark'
  return 'System'
}

function resolveAppearance(theme: Appearance): ResolvedAppearance {
  if (theme === 'light' || theme === 'dark') {
    return theme
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function useResolvedAppearance(): ResolvedAppearance {
  const { theme } = useTheme()
  const [resolved, setResolved] = useState<ResolvedAppearance>(() => resolveAppearance(theme))

  useEffect(() => {
    if (theme === 'light' || theme === 'dark') {
      setResolved(theme)
      return
    }

    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const sync = () => setResolved(mq.matches ? 'dark' : 'light')
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [theme])

  return resolved
}

type AppearanceToggleProps = {
  variant?: 'app' | 'marketing'
  size?: 'default' | 'compact'
  /** Resolved light/dark for chrome that depends on the active surface. */
  marketingTheme?: 'light' | 'dark'
  /** Selected preference (includes system). */
  marketingPreference?: MarketingThemePreference
  onMarketingThemeChange?: (t: MarketingThemePreference) => void
  overlayTrack?: boolean
}

function MarketingAppearanceCycle({
  size = 'default',
  marketingPreference = 'system',
  onMarketingThemeChange,
  overlayTrack = false,
}: {
  size?: 'default' | 'compact'
  marketingPreference?: MarketingThemePreference
  onMarketingThemeChange?: (t: MarketingThemePreference) => void
  overlayTrack?: boolean
}) {
  const compact = size === 'compact'
  const label = marketingPreferenceLabel(marketingPreference)
  const nextLabel = marketingPreferenceLabel(nextMarketingPreference(marketingPreference))
  const Icon =
    marketingPreference === 'light' ? Sun : marketingPreference === 'dark' ? Moon : Contrast
  const reducedMotion = useReducedMotion()

  return (
    <Button
      type="button"
      variant="ghost"
      size={compact ? 'iconSm' : 'icon'}
      pressFeedback
      aria-label={`Appearance: ${label}. Activate to switch to ${nextLabel}. Cycles Light, Dark, System.`}
      title={`${label} · click for ${nextLabel}`}
      className={cn(
        'relative rounded-full border shadow-none after:absolute after:left-1/2 after:top-1/2 after:size-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-[""]',
        compact ? 'size-8' : 'size-9',
        overlayTrack
          ? 'border-white/50 bg-transparent text-white hover:bg-white/10 hover:text-white'
          : 'border-[var(--marketing-border,var(--border))] bg-[var(--marketing-bg,var(--background))] text-[var(--marketing-text,var(--foreground))] hover:bg-[var(--marketing-bg,var(--background))] hover:text-[var(--marketing-text,var(--foreground))]'
      )}
      onClick={(e) => {
        e.stopPropagation()
        onMarketingThemeChange?.(nextMarketingPreference(marketingPreference))
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {reducedMotion ? (
        <Icon className={compact ? 'size-3.5' : 'size-4'} aria-hidden />
      ) : (
        <AnimatePresence initial={false} mode="popLayout">
          <motion.span
            key={marketingPreference}
            className="inline-flex"
            initial={{ opacity: 0, scale: 0.25, filter: 'blur(4px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.25, filter: 'blur(4px)' }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
          >
            <Icon className={compact ? 'size-3.5' : 'size-4'} aria-hidden />
          </motion.span>
        </AnimatePresence>
      )}
    </Button>
  )
}

export function AppearanceToggle({
  variant = 'app',
  size = 'default',
  marketingPreference,
  onMarketingThemeChange,
  overlayTrack = false,
}: AppearanceToggleProps) {
  const { theme, setTheme } = useTheme()
  const resolved = useResolvedAppearance()
  const isMarketing = variant === 'marketing'

  if (isMarketing) {
    return (
      <MarketingAppearanceCycle
        size={size}
        marketingPreference={marketingPreference ?? 'system'}
        onMarketingThemeChange={onMarketingThemeChange}
        overlayTrack={overlayTrack}
      />
    )
  }

  return (
    <ToggleGroup
      type="single"
      value={theme}
      onValueChange={(v) => {
        if (!v) return
        setTheme(v as Appearance)
      }}
      variant="default"
      size="sm"
      className={cn(
        'inline-flex w-[84px] items-center justify-between rounded-full border p-0.5',
        guestTrackFilledClassName
      )}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <ToggleGroupItem
        value="system"
        aria-label="System"
        title="System"
        className={toggleItemClassName}
      >
        <Monitor className="size-3.5" aria-hidden />
      </ToggleGroupItem>
      <ToggleGroupItem
        value="light"
        aria-label="Light"
        title="Light"
        className={toggleItemClassName}
      >
        <Sun className="size-3.5" aria-hidden />
      </ToggleGroupItem>
      <ToggleGroupItem value="dark" aria-label="Dark" title="Dark" className={toggleItemClassName}>
        <Moon className="size-3.5" aria-hidden />
      </ToggleGroupItem>
      <span className="sr-only">Current appearance: {resolved}</span>
    </ToggleGroup>
  )
}
